<template>
	<div class="container_section px-5 pb-5 pt-2">
		<!-- Header: título + slot para acciones extra (Comparación, Ver Barra...)
			+ selector de período. El selector se oculta cuando filterPeriod es
			YTD/year porque el rango ya es implícito. -->
		<div class="table_header is-flex is-justify-content-space-between is-align-items-center mb-2">
			<!-- slot title sobreescribe la prop simple cuando el consumidor
				necesita componer ícono + texto (Rentabilidad). El fallback
				conserva el comportamiento original de ResultadosTab. -->
			<div class="is-flex is-align-items-center">
				<slot name="title">
					<span class="tittle_section">{{ title }}</span>
				</slot>
			</div>
			<div class="table_actions is-flex is-align-items-center">
				<slot name="header-actions"></slot>
				<div v-if="periodSelectVisible" style="min-width: 165px">
					<SelectComponent
						:value="selectedPeriodRange"
						@input="(val) => $emit('update-period', val)"
						theme="report_theme"
						:options="periodOptions" />
				</div>
			</div>
		</div>

		<section>
			<div class="table_container" ref="tableScroll" v-if="!isLoading">
				<table class="custom_table">
					<colgroup>
						<col style="width: 200px;">
						<col style="width: 160px;" v-for="header in headers" :key="header">
					</colgroup>
					<thead>
						<tr>
							<th scope="col" class="sticky-column header_label nobreak"></th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in headers" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in renderRows" :key="row.id">
							<th scope="row" class="sticky-column" :class="row.theme || 'theme_row_white'">
								<!-- Celda flex: el label puede envolver a 2 líneas (cuentas
									de balance largas) y el chevron queda SIEMPRE pegado a la
									derecha y centrado vertical — con float (is-pulled-right)
									caía debajo del texto al envolver. -->
								<div class="label_cell">
									<span class="row_label"
										:class="{ navigable: navigable && row.targetTab }"
										@click="onLabelClick(row)">{{ row.label }}</span>
									<!-- chevron data-driven: cualquier fila con hasChilds emite
										el toggle. El padre maneja el estado expandido. .stop
										para que el toggle no dispare también la navegación. -->
									<span v-if="row.hasChilds"
										class="actual_button_arrow_down"
										@click.stop="$emit('toggle-childs', row.id)">
										<ArrowDown class="color_arrow" :class="{ rotate_arrow: expandedRows.includes(row.id) }" />
									</span>
								</div>
							</th>
							<td v-for="(header, idx) in headers" :key="header" class="has-text-right nobreak"
								:class="[row.theme || 'theme_row_white', { navigable_cell: navigable && row.targetTab }]"
								@click="onCellClick(row, header)">
								<!-- Tooltip per-cell (opt-in vía cellTooltips) con la lectura
									temporal MoM/QoQ, anclado al número como en Ventas/Gastos.
									Sólo en filas de dinero: la variación de una fila de ratio
									(márgenes %, gastos/ventas) sería engañosa — mismo criterio
									que las filas de ratio de ExpensesEvolutionTable. El YoY no
									se muestra porque el reporte de Resultados no expone peer
									del año anterior (el util omite esa línea sin prevYearValues). -->
								<!-- Fila de validación (analistas): celda que cuadra → ✓;
									descuadre → el monto de la diferencia resaltado, para
									ubicar el período a resolver de un vistazo. -->
								<template v-if="row.isValidation">
									<span v-if="isBalancedCell(row.itemsDict[header])" class="validation_ok">✓</span>
									<span v-else class="validation_off">{{ money(row.itemsDict[header] || 0) }}</span>
								</template>
								<template v-else-if="row.isMoney">
									<span v-if="cellTooltips" class="cell_value"
										v-tooltip.top="cellVariationTooltip(row, idx, filterPeriod)">
										{{ money(row.itemsDict[header] || 0) }}
									</span>
									<template v-else>{{ money(row.itemsDict[header] || 0) }}</template>
								</template>
								<template v-else>
									{{ Math.round(row.itemsDict[header] || 0) }}%
								</template>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div v-else class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { cellVariationTooltip } from "@/views/financialReports/utils/cellTooltip";
import SelectComponent from "@/components/classification/SelectComponent";
import ArrowDown from "@/components/svg/ArrowDown.vue";

export default {
	name: "FinancialResultsTable",
	components: { SelectComponent, ArrowDown },
	props: {
		title: { type: String, default: "" },
		rows: { type: Array, required: true },
		headers: { type: Array, required: true },
		isLoading: { type: Boolean, default: false },
		selectedPeriodRange: { type: String, default: "" },
		periodOptions: { type: Array, default: () => [] },
		filterPeriod: { type: String, default: "month" },
		expandedRows: { type: Array, default: () => [] },
		// Opt-out del selector de período in-table. Default true (Rentabilidad lo
		// usa). Resultados lo apaga porque su rango se maneja desde el calendar
		// del header (modo range), igual que Ventas.
		showPeriodSelect: { type: Boolean, default: true },
		// Opt-in: hace navegables las filas. Cuando true, las filas que traen
		// `targetTab` muestran afordancia y emiten `navigate-row` (click en el
		// nombre) / `navigate-cell` (click en una celda). El padre resuelve el
		// salto de tab. Default false → Rentabilidad no cambia.
		navigable: { type: Boolean, default: false },
		// Opt-in: tooltip per-cell con la variación MoM/QoQ (como Ventas/Gastos),
		// sólo en filas de dinero. Default false → Rentabilidad no cambia.
		cellTooltips: { type: Boolean, default: false },
	},
	computed: {
		// YTD y year ya implican un rango fijo (año en curso, año cerrado);
		// mostrar un selector adicional confunde. Además el consumidor puede
		// apagarlo del todo con `showPeriodSelect=false`.
		periodSelectVisible() {
			return this.showPeriodSelect && !["ytd", "year"].includes(this.filterPeriod);
		},
		// Filas con arrays alineados a `headers` que el tooltip per-cell consume:
		// `values` (lee `values[idx]` y `values[idx-1]` para MoM/QoQ) y, cuando la
		// fila trae `prevYearDict` (peer 12m atrás), `prevYearValues` para el YoY.
		// La tabla guarda los montos keyed por header; los proyectamos a arrays
		// una vez por render en lugar de reconstruirlos en cada celda. Sólo aplica
		// con cellTooltips y a filas de dinero — el resto pasa igual.
		renderRows() {
			if (!this.cellTooltips) return this.rows;
			return this.rows.map((row) => {
				if (!row.isMoney) return row;
				const projected = {
					...row,
					values: this.headers.map((h) => Number(row.itemsDict[h] || 0)),
				};
				// prevYearValues opt-in: su presencia es la que enciende la línea
				// YoY en el tooltip (cellVariationTooltip la omite sin el array).
				if (row.prevYearDict) {
					projected.prevYearValues = this.headers.map((h) => Number(row.prevYearDict[h] || 0));
				}
				// prevPeriodValues: peer real del período anterior (MoM/QoQ). Su
				// presencia hace que el tooltip use data real en vez de la columna
				// visible previa, dándole peer a la primera columna.
				if (row.prevPeriodDict) {
					projected.prevPeriodValues = this.headers.map((h) => Number(row.prevPeriodDict[h] || 0));
				}
				return projected;
			});
		},
	},
	watch: {
		rows() { this.scrollToRight(); },
		isLoading(val) { if (!val) this.scrollToRight(); },
	},
	methods: {
		money(val) { return parseToMoney(val); },
		cellVariationTooltip,
		// Tolerancia de la fila de validación: bajo 1 peso es ruido de
		// redondeo/float, no un descuadre.
		isBalancedCell(value) {
			return Math.abs(Number(value) || 0) < 1;
		},
		// Click en el nombre de la fila → navega al tab de esa fila (sin modal).
		// Sólo las filas con `targetTab` son navegables (EBITDA/Utilidad no).
		onLabelClick(row) {
			if (!this.navigable || !row.targetTab) return;
			this.$emit("navigate-row", { rowId: row.id });
		},
		// Click en una celda de valor → navega al tab + (en el padre) abre el
		// modal de ese mes. El padre decide si corresponde modal según el tab y
		// la granularidad.
		onCellClick(row, header) {
			if (!this.navigable || !row.targetTab) return;
			this.$emit("navigate-cell", { rowId: row.id, monthLabel: header });
		},
		scrollToRight() {
			this.$nextTick(() => {
				const el = this.$refs.tableScroll;
				if (el) el.scrollLeft = el.scrollWidth;
			});
		},
	},
};
</script>

<style scoped lang="scss">
.container_section {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}

.table_actions {
	gap: 8px;
}

.table_container {
	overflow-x: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	background: var(--report-bg);
	max-width: 100%;
}

.sticky-column {
	position: sticky;
	left: 0;
	z-index: 2;
	background-color: inherit;
	border-right: 1px solid var(--report-border);
	// El <col> de 200px es sólo una sugerencia con table-layout auto: cuando
	// los meses empujan el ancho total, el browser achicaba esta columna y los
	// labels quedaban a una palabra por línea. min-width la hace firme.
	min-width: 200px;
}

// Layout de la celda de labels: texto (envuelve si hace falta) + chevron
// fijo a la derecha, centrado vertical.
.label_cell {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;

	.row_label {
		min-width: 0;
	}

	.actual_button_arrow_down {
		flex-shrink: 0;
	}
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	td, th {
		height: 44px;
		padding: 12px 16px !important;
		border-bottom: 1px solid var(--report-divider-soft);
		vertical-align: middle;
	}

	thead th, td { text-align: end; }

	thead th {
		padding: 12px 24px;
		// Header sólido: banda con el primary del tab + texto blanco. Vía tokens.
		color: var(--report-bg);
		font-size: 12px;
		font-weight: 700;
		line-height: 18px;
		background-color: var(--report-primary);
	}

	// Corner sticky (esquina sobre la columna de labels): primary más oscuro
	// que la banda de meses, para anclar el marco sticky.
	.header_label {
		background-color: var(--report-primary-hover);
	}

	tbody th {
		color: var(--report-text);
		font-size: 12px;
		font-weight: 500;
		line-height: 18px;
	}

	tbody td {
		color: var(--report-text-muted-strong);
		font-size: 14px;
		font-weight: 400;
		line-height: 20px;
	}

	// Ancla del tooltip per-cell: la caja envuelve sólo el número para que el
	// tooltip aparezca sobre el texto (alineado a la derecha) y no centrado en
	// un <td> ancho. Mismo patrón que Sales/ExpensesEvolutionTable.
	.cell_value {
		display: inline-block;
	}

	// Afordancia de navegación (sólo con `navigable` + fila con targetTab).
	// El nombre se subraya al hover; la celda de valor cambia el fondo al
	// accent del tab, mismo lenguaje que el drill de SalesEvolutionTable.
	.row_label.navigable {
		cursor: pointer;

		&:hover { text-decoration: underline; }
	}

	.navigable_cell {
		cursor: pointer;
		transition: background-color 0.12s ease;

		&:hover { background-color: var(--report-accent-bg); }
	}

	// Fila de validación contable (analistas): fila de control, no de datos —
	// fondo neutro con separador superior marcado.
	.theme_row_validation {
		background-color: var(--report-bg);
		border-top: 2px solid var(--report-border);
	}

	.validation_ok {
		color: var(--report-text-muted);
		font-weight: 600;
	}

	.validation_off {
		color: var(--report-negative);
		font-weight: 700;
	}

	.theme_row_white { background-color: var(--report-bg); }
	.theme_row_blue { background-color: var(--report-row-blue); }
	.theme_row_azure { background-color: var(--report-row-azure); }
	.theme_row_yellow { background-color: var(--report-row-yellow); }
	.theme_row_purple { background-color: var(--report-row-purple); }
	.theme_row_orange { background-color: var(--report-row-orange); }
	.theme_row_detail_bills {
		padding-left: 3em !important;
		background: var(--report-row-cream);
	}
}

.actual_button_arrow_down {
	width: 18px;
	height: 18px;
	vertical-align: middle;
	cursor: pointer;
}

.color_arrow {
	filter: brightness(0) saturate(100%) invert(20%) sepia(20%) saturate(500%) hue-rotate(180deg);
}

.rotate_arrow {
	transform: rotate(180deg);
}
</style>
